import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { setAuthToken } from '../api/client';

// Auth service configuration
const AUTH_URL = import.meta.env.VITE_AUTH_URL || 'https://auth.wallyco.net';

export interface User {
  id: string;
  email: string;
  name: string;
  picture?: string;
}

interface JwtPayload {
  sub: string;
  email: string;
  name: string;
  iat: number;
  exp: number;
  iss: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  login: () => void;
  logout: () => Promise<void>;
  logoutAll: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Decode a JWT token without verification (client-side)
 */
function decodeToken(token: string): JwtPayload | null {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;

    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

/**
 * Check if a token is expired (with 60 second buffer)
 */
function isTokenExpired(token: string): boolean {
  const payload = decodeToken(token);
  if (!payload) return true;

  const now = Math.floor(Date.now() / 1000);
  return payload.exp < now + 60; // 60 second buffer
}

/**
 * Extract user info from JWT payload
 */
function getUserFromPayload(payload: JwtPayload): User {
  return {
    id: payload.sub,
    email: payload.email,
    name: payload.name,
  };
}

/**
 * Parse authorization code from URL query params and clean URL
 */
function parseAuthCode(): string | null {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  if (!code) return null;

  const remaining = new URLSearchParams(window.location.search);
  remaining.delete('code');
  const newSearch = remaining.toString();
  window.history.replaceState(null, '', window.location.pathname + (newSearch ? '?' + newSearch : ''));

  return code;
}

/**
 * Exchange authorization code for access token
 */
async function exchangeCode(code: string): Promise<string | null> {
  try {
    const response = await fetch('https://auth.wallyco.net/auth/token/exchange', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    });
    if (!response.ok) return null;
    const data = await response.json();
    return data.access_token ?? null;
  } catch {
    return null;
  }
}

/**
 * Parse error from URL query params
 */
function parseAuthError(): string | null {
  const params = new URLSearchParams(window.location.search);
  const error = params.get('error');

  if (error) {
    // Clear error from URL
    window.history.replaceState(null, '', window.location.pathname);

    switch (error) {
      case 'unauthorized_domain':
        return 'Only @wayfong.com and @wallyco.net accounts are allowed.';
      case 'invalid_state':
        return 'Authentication session expired. Please try again.';
      case 'no_code':
        return 'Authentication was cancelled or failed.';
      default:
        return 'Authentication failed. Please try again.';
    }
  }

  return null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setTokenState] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshTimer, setRefreshTimer] = useState<ReturnType<typeof setTimeout> | null>(null);

  const setToken = useCallback((newToken: string | null) => {
    setTokenState(newToken);
  }, []);

  /**
   * Refresh the access token using the HTTP-only refresh cookie
   */
  const refreshAccessToken = useCallback(async (): Promise<string | null> => {
    try {
      const response = await fetch(`${AUTH_URL}/auth/token/refresh`, {
        method: 'POST',
        credentials: 'include', // Send cookies
      });

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      return data.accessToken;
    } catch {
      return null;
    }
  }, []);

  /**
   * Setup automatic token refresh before expiry
   */
  const setupTokenRefresh = useCallback((expiresIn: number) => {
    // Clear existing timer
    if (refreshTimer) {
      clearTimeout(refreshTimer);
    }

    // Refresh 60 seconds before expiry
    const refreshTime = Math.max((expiresIn - 60) * 1000, 10000);

    const timer = setTimeout(async () => {
      const newToken = await refreshAccessToken();
      if (newToken) {
        const payload = decodeToken(newToken);
        if (payload) {
          setToken(newToken);
          setAuthToken(newToken); // Sync with API client
          setUser(getUserFromPayload(payload));
          const newExpiresIn = payload.exp - Math.floor(Date.now() / 1000);
          setupTokenRefresh(newExpiresIn);
        }
      } else {
        // Refresh failed, clear auth state
        setToken(null);
        setAuthToken(null);
        setUser(null);
      }
    }, refreshTime);

    setRefreshTimer(timer);
  }, [refreshTimer, refreshAccessToken]);

  /**
   * Initialize auth state on mount
   */
  useEffect(() => {
    const initAuth = async () => {
      // Check for auth error in URL
      const authError = parseAuthError();
      if (authError) {
        setError(authError);
        setLoading(false);
        return;
      }

      // Check for authorization code in URL query params (OAuth callback)
      const code = parseAuthCode();
      if (code) {
        const accessToken = await exchangeCode(code);
        if (accessToken) {
          const payload = decodeToken(accessToken);
          if (payload && payload.iss === 'auth.wallyco.net') {
            setToken(accessToken);
            setAuthToken(accessToken); // Sync with API client
            setUser(getUserFromPayload(payload));
            const expiresIn = payload.exp - Math.floor(Date.now() / 1000);
            setupTokenRefresh(expiresIn);
            setLoading(false);
            return;
          }
        }
        setError('Authentication failed. Please try again.');
        setLoading(false);
        return;
      }

      // Try to refresh existing session via HTTP-only cookie
      const newToken = await refreshAccessToken();
      if (newToken) {
        const payload = decodeToken(newToken);
        if (payload) {
          setToken(newToken);
          setAuthToken(newToken); // Sync with API client
          setUser(getUserFromPayload(payload));
          const expiresIn = payload.exp - Math.floor(Date.now() / 1000);
          setupTokenRefresh(expiresIn);
        }
      }

      setLoading(false);
    };

    initAuth();

    // Cleanup timer on unmount
    return () => {
      if (refreshTimer) {
        clearTimeout(refreshTimer);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Redirect to wayfong-auth for login
   */
  const login = useCallback(() => {
    // Use full URL (origin + pathname) so user returns to /sales if that's where they started
    const currentPath = window.location.pathname;
    const redirectUri = encodeURIComponent(window.location.origin + currentPath);
    window.location.href = `${AUTH_URL}/auth/google?redirect_uri=${redirectUri}`;
  }, []);

  /**
   * Logout current session
   */
  const logout = useCallback(async () => {
    try {
      await fetch(`${AUTH_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // Ignore errors, still clear local state
    }

    if (refreshTimer) {
      clearTimeout(refreshTimer);
    }
    setToken(null);
    setAuthToken(null); // Clear API client token
    setUser(null);
    setError(null);
  }, [refreshTimer]);

  /**
   * Logout from all sessions
   */
  const logoutAll = useCallback(async () => {
    try {
      await fetch(`${AUTH_URL}/auth/logout/all`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // Ignore errors, still clear local state
    }

    if (refreshTimer) {
      clearTimeout(refreshTimer);
    }
    setToken(null);
    setAuthToken(null); // Clear API client token
    setUser(null);
    setError(null);
  }, [refreshTimer]);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated: !!token && !isTokenExpired(token),
        loading,
        error,
        login,
        logout,
        logoutAll,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
